import { getBounds, isBrowser, isDefined, isNotDefined, type RoundingFunction } from '@enterprise/core';

import type { ResizeCallback } from '#utilities/types/resize.callback.type';

import type { ResizeObserverSingleton } from './types/resize-observer.singleton';

/**
 * Returns a singleton instance of a `ResizeObserver`-based utility for observing element resize events.
 *
 * This function ensures that only one instance of the resize observer exists globally
 * (attached to `globalThis.enterpriseResizeObserver`).
 * It allows registering and de-registering resize callbacks for specific DOM elements.
 *
 * @param round - Optional rounding function to apply to the bounding box dimensions returned by `getBounds`.
 * @returns A `ResizeObserverSingleton` instance with `add` and `remove` methods for managing resize callbacks,
 * or `undefined` if not running in a browser environment.
 *
 * @remarks
 * - Uses `requestAnimationFrame` to batch callback invocations for performance.
 * - Automatically observes and un-observes elements as callbacks are added or removed.
 * - Requires browser environment (`isBrowser` check).
 */
export const getResizeObserver = (round?: RoundingFunction): ResizeObserverSingleton | undefined => {
  if (!isBrowser) {
    return undefined;
  }

  if (isDefined(globalThis.enterpriseResizeObserver)) {
    return globalThis.enterpriseResizeObserver;
  }

  const callbacks = new Map<Element, Set<ResizeCallback>>();
  const observer = new ResizeObserver((entries) => {
    requestAnimationFrame(() => {
      for (const { target } of entries) {
        const resizeCallbacks = callbacks.get(target);

        if (isDefined(resizeCallbacks)) {
          for (const callback of resizeCallbacks) {
            callback(getBounds(target, round));
          }
        }
      }
    });
  });

  const resizeObserver: ResizeObserverSingleton = {
    add: (target, callback) => {
      let resizeCallbacks = callbacks.get(target);

      if (isNotDefined(resizeCallbacks)) {
        resizeCallbacks = new Set<ResizeCallback>();
        callbacks.set(target, resizeCallbacks);
        observer.observe(target);
      }

      resizeCallbacks.add(callback);
    },
    remove: (target, callback) => {
      const resizeCallbacks = callbacks.get(target);

      if (isDefined(resizeCallbacks)) {
        resizeCallbacks.delete(callback);

        if (resizeCallbacks.size === 0) {
          callbacks.delete(target);
          observer.unobserve(target);
        }
      }
    },
  };

  globalThis.enterpriseResizeObserver = resizeObserver;

  return resizeObserver;
};
